/**
 * WorkspaceSwitcher — модал «переключиться между рабочими страницами».
 *
 * Открывается по long-press / right-click на аватарке профиля.
 * Карточки тех сабдоменов, к которым у юзера есть доступ
 * (через hasAccess). Клик → cross-domain переход.
 *
 * Срабатывает на CustomEvent("kb-open-workspace-switcher") — диспатчат
 * FolderRail.ProfileRailButton (десктоп right-click) и MobileProfileFab
 * (мобильный long-press).
 */
import { useEffect, useState } from "react";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { BRAND, type BrandKey } from "@/lib/setBrandFavicon";


interface Workspace {
  key: BrandKey;
  title: string;
  subtitle: string;
  /** Сабдомен-prefix (sales / production / finance / warehouse / messenger). */
  subdomain: string;
  /** hasAccess area для проверки прав. */
  access: Parameters<typeof hasAccess>[1];
}


const WORKSPACES: Workspace[] = [
  { key: "sales",      title: "Отдел продаж", subtitle: "Сводка, менеджеры, звонки", subdomain: "sales",      access: "sales" },
  { key: "messenger",  title: "Мессенджер",   subtitle: "Чат, диск, бот зарплаты",   subdomain: "messenger",  access: "messenger" },
  { key: "production", title: "Производство", subtitle: "Календарь, выезды, лог",    subdomain: "production", access: "production" },
  { key: "finance",    title: "Финансы",      subtitle: "P&L, ДДС, ФОТ",             subdomain: "finance",    access: "finance" },
  { key: "warehouse",  title: "Склад",        subtitle: "Материалы, остатки",        subdomain: "warehouse",  access: "supply" },
];


export function WorkspaceSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    document.addEventListener("kb-open-workspace-switcher", onOpen);
    return () => document.removeEventListener("kb-open-workspace-switcher", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  if (!user) return null;

  const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
  const currentSubdomain =
    currentHost.startsWith("sales.")      ? "sales" :
    currentHost.startsWith("production.") ? "production" :
    currentHost.startsWith("finance.")    ? "finance" :
    currentHost.startsWith("warehouse.")  ? "warehouse" :
    currentHost.startsWith("messenger.")  ? "messenger" :
    "sales";

  const available = WORKSPACES.filter(w => hasAccess(user, w.access));

  function go(w: Workspace) {
    if (w.subdomain === currentSubdomain) { setOpen(false); return; }
    const h = window.location.hostname;
    const base = h.replace(/^(sales|production|finance|warehouse|messenger|dashboard)\./, "");
    const target = `https://${w.subdomain}.${base}/`;
    window.location.href = target;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 10010,
        background: "rgba(0,0,0,0.45)",
        display: "grid", placeItems: "center",
        padding: 16,
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        animation: "ws-fade 120ms ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 18,
          maxWidth: 480, width: "100%",
          padding: "20px 18px 18px",
          boxShadow: "0 16px 48px rgba(0,0,0,0.30)",
          fontFamily:
            'Inter Tight, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          color: "#222",
          animation: "ws-pop 160ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <div style={{
          fontSize: 11, fontWeight: 600, color: "#9e9e9e",
          textTransform: "uppercase", letterSpacing: 0.5,
          marginBottom: 4,
        }}>Перейти в раздел</div>
        <div style={{
          fontSize: 18, fontWeight: 700, color: "#1a1a1a",
          marginBottom: 14,
        }}>Кирпичные беседки</div>

        {available.length === 0 ? (
          <div style={{
            padding: 24, textAlign: "center", color: "#707579",
            background: "#f4f4f5", borderRadius: 10,
            fontSize: 13,
          }}>
            У вас нет доступа к другим разделам. Запросите у администратора.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {available.map(w => {
              const spec = BRAND[w.key];
              const isCurrent = w.subdomain === currentSubdomain;
              return (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => go(w)}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "12px 14px",
                    background: isCurrent ? "#f4f4f5" : "#fff",
                    border: `1px solid ${isCurrent ? "#e0e0e0" : "#e6e6e6"}`,
                    borderRadius: 14,
                    cursor: isCurrent ? "default" : "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    transition: "transform 120ms, box-shadow 120ms, border-color 120ms",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    if (isCurrent) return;
                    (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 14px rgba(0,0,0,0.06)";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = spec.from;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = "";
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = "";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#e6e6e6";
                  }}
                >
                  <svg width={42} height={42} viewBox="0 0 64 64" style={{
                    flexShrink: 0, borderRadius: 12,
                    boxShadow: `0 4px 10px ${spec.from}40`,
                  }}>
                    <defs>
                      <linearGradient id={`ws_${w.key}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={spec.from}/>
                        <stop offset="100%" stopColor={spec.to}/>
                      </linearGradient>
                    </defs>
                    <rect width="64" height="64" rx="14" fill={`url(#ws_${w.key})`}/>
                    <g fill="none" stroke="white" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round">
                      <path d={spec.iconPath}/>
                    </g>
                  </svg>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 15, fontWeight: 700, color: "#1a1a1a",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{w.title}</div>
                    <div style={{
                      fontSize: 12, color: "#707579", marginTop: 2, lineHeight: 1.3,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{w.subtitle}</div>
                  </div>
                  {isCurrent ? (
                    <span style={{
                      fontSize: 10, fontWeight: 600, color: spec.from,
                      padding: "2px 8px", borderRadius: 10,
                      background: `${spec.from}1a`,
                      textTransform: "uppercase", letterSpacing: 0.3,
                      flexShrink: 0,
                    }}>сейчас</span>
                  ) : (
                    <span aria-hidden="true" style={{ color: "#9e9e9e", fontSize: 18, flexShrink: 0 }}>›</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 11, color: "#9e9e9e" }}>
            Esc — закрыть
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              padding: "6px 14px", fontSize: 13, fontWeight: 500,
              background: "transparent", border: "1px solid #d4d4d8",
              borderRadius: 8, cursor: "pointer",
              color: "#444", fontFamily: "inherit",
            }}
          >Отмена</button>
        </div>

        <style>{`
          @keyframes ws-fade { from {opacity:0} to {opacity:1} }
          @keyframes ws-pop {
            from { opacity:0; transform: scale(0.96) translateY(8px) }
            to   { opacity:1; transform: scale(1) translateY(0) }
          }
        `}</style>
      </div>
    </div>
  );
}
